/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { getContentId, useMediaAnalytics, useQueue } from "@packages/media";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  constantSemanticColors,
  numericValues,
} from "../../../../../../global/emotion";
import { Button } from "../../../../../components/_base/_core/Button/Button.emotion";
import {
  RepeatIcon,
  RepeatOneIcon,
  ShuffleIcon,
} from "../../../../../components/_base/icons/Icon/MediaIcons";
import type { WithButtonWrapper } from "../../../../../types/withButtonWrapper.types";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild/FragmentAsChild";
import { PlayPause } from "../../controls/PlayPause/PlayPause.emotion";

const defaultStyle: Interpolation = {
  border: numericValues[0],
  color: constantSemanticColors.onColorHigh,
  height: "min-content",
  padding: 0,
  width: "auto",
};
const activeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});

export type QueueControlsProps = WithButtonWrapper;

export const QueueControls = ({
  Wrapper = FragmentAsChild,
}: QueueControlsProps) => {
  const queue = useQueue();
  const mediaAnalytics = useMediaAnalytics();
  const analytics = useAnalytics();

  return (
    <>
      {queue.currentItem?.type === "radio_song" ? null : (
        <Wrapper asChild>
          <Button
            variant={"transparentWhite"}
            onClick={() => {
              const newShuffleValue = queue.shuffle();
              analytics.track({
                event: HallowAnalyticsEvent.TappedShuffle,
                properties: {
                  shuffled: newShuffleValue,
                  content_id: getContentId(queue.currentItem),
                  content_type: queue.currentItem.type,
                },
              });
            }}
            css={[defaultStyle, queue.shuffled ? activeStyle : null]}
          >
            <ShuffleIcon />
          </Button>
        </Wrapper>
      )}
      <Wrapper asChild>
        <PlayPause size={"l"} />
      </Wrapper>
      {queue.currentItem?.type === "radio_song" ? null : (
        <Wrapper asChild>
          <Button
            variant={"transparentWhite"}
            onClick={() => {
              const newLoopValue = queue.repeat();
              mediaAnalytics.current?.loopChanged(newLoopValue);
            }}
            css={[
              defaultStyle,
              queue.repeating !== "none" ? activeStyle : null,
            ]}
          >
            {queue.repeating === "one" ? <RepeatOneIcon /> : <RepeatIcon />}
          </Button>
        </Wrapper>
      )}
    </>
  );
};
